import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getAccommodationsBlurPlaceholder } from "@/utils/accommodationsBlurPlaceholders";
import { Room } from "@/types/room";
import {
  Users,
  Square,
  Bed,
  Bath,
  ChevronLeft,
  ChevronRight,
  Mountain,
  Wifi,
  Tv,
  Wind,
  ChefHat,
  Shield,
  Star,
  Share2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useViewportAutoplay } from "@/hooks/useViewportAutoplay";

interface TripleRoomSectionProps {
  room: Room;
  onViewDetails?: (roomId: string) => void;
  onQuickBook?: (roomId: string) => void;
}

const TripleRoomSection: React.FC<TripleRoomSectionProps> = ({
  room,
  onViewDetails,
  onQuickBook,
}) => {
  const { t } = useLanguage();

  // Viewport-based auto-play hook
  const {
    currentImageIndex,
    isAutoPlaying,
    elementRef,
    handlePreviousImage,
    handleNextImage,
    handleImageClick,
  } = useViewportAutoplay({
    imageCount: room.images.length,
    intervalMs: 4000,
    threshold: 0.7,
  });

  const handleViewDetails = () => {
    onViewDetails?.(room.id);
  };

  const handleQuickBook = () => {
    onQuickBook?.(room.id);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/accommodations#triple-rooms`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${room.name} - Park Hotel Beatenberg`,
          text: `Check out our Triple Rooms with comfortable accommodation and Alpine views`,
          url: url,
        });
      } catch (error) {
        // Fallback to clipboard if share is cancelled
        copyToClipboard(url);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(url);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log("Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const currentImage = room.images[currentImageIndex];

  // Feature badges data
  const featureBadges = [
    {
      icon: Users,
      label: "Occupancy",
      value: t("rooms.tripleRoomBadges.occupancy"),
      description: `${room.capacity.adults} adults, ${room.capacity.children} children`,
    },
    {
      icon: Square,
      label: "Room Size",
      value: t("rooms.tripleRoomBadges.roomSize"),
      description: `${room.size.squareFeet} sq ft`,
    },
    {
      icon: Bed,
      label: "Bedding",
      value: t("rooms.tripleRoomBadges.bedding"),
      description: "Flexible configuration",
    },
    {
      icon: Bath,
      label: "Bathrooms",
      value: t("rooms.tripleRoomBadges.bathrooms"),
      description: "Ensuite bathroom",
    },
  ];

  // Amenities for text badges
  const amenityBadges = [
    "Perfect for small families",
    "Scenic balcony with mountain, lake, or village view",
  ];

  return (
    <section
      id="triple-rooms"
      className="py-16 bg-gradient-to-b from-white to-gray-50/50 relative overflow-hidden"
      data-oid="a10e3q4"
    >
      {/* Background Quadrilaterals */}
      <div
        className="absolute top-0 right-0 w-96 h-96 transform rotate-45 translate-x-48 -translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="lbi2z1u"
      ></div>
      <div
        className="absolute bottom-0 left-0 w-96 h-96 transform rotate-45 -translate-x-48 translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="fi2o:k2"
      ></div>
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        data-oid="cp9yopg"
      >
        {/* Mobile Header - Shown only on mobile */}
        <div className="mb-8 lg:hidden" data-oid=":0:f754">
          <div className="mb-4" data-oid="qj9-gr3">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-playfair text-gray-900"
              data-oid="zqkk6v9"
            >
              {t("rooms.tripleRoom")}
            </h2>
          </div>

          {/* Key badges below title - Mobile */}
          <div
            className="flex items-center gap-3 sm:gap-4 md:gap-6 flex-wrap"
            data-oid="q154xky"
          >
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="8wve0--"
            >
              <Users
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="ge.i3yt"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="zn797zy"
              >
                {featureBadges[0].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="0kq4.z_"
            >
              <Square
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="p27z6n0"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="qezr-t1"
              >
                {featureBadges[1].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="s4.ison"
            >
              <Bed
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="0jxw7g9"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="wnjh1mq"
              >
                {featureBadges[2].value}
              </span>
            </div>
          </div>
        </div>

        {/* 2-Column Layout: Carousel Left (60%), Content Right (40%) */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[50%_50%] xl:grid-cols-[55%_45%] 2xl:grid-cols-[60%_40%] gap-6 lg:gap-8 xl:gap-12 mb-12"
          data-oid="t7hocad"
        >
          {/* Left Column - Image Carousel */}
          <div
            ref={elementRef}
            className="relative w-full aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden rounded-2xl shadow-2xl"
            data-oid="c5r7nvn"
          >
            <div
              className="flex h-full transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
              data-oid="r:h:woo"
            >
              {room.images.map((image, index) => (
                <div
                  key={image.id}
                  className="w-full h-full flex-shrink-0"
                  data-oid="fkj8gb_"
                >
                  <OptimizedImage
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    priority={false}
                    rootMargin={index === 0 ? "300px" : "200px"}
                    blurDataURL={getAccommodationsBlurPlaceholder(image.url)}
                    responsive={true}
                    data-oid="7byi-it"
                  />
                </div>
              ))}
            </div>

            {/* Image Navigation */}
            {room.images.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handlePreviousImage}
                  aria-label="Previous image"
                  data-oid="7dlmp5w"
                >
                  <ChevronLeft className="w-5 h-5" data-oid="dvl_au5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handleNextImage}
                  aria-label="Next image"
                  data-oid="cb6kwo9"
                >
                  <ChevronRight className="w-5 h-5" data-oid="1afrx6r" />
                </Button>
              </>
            )}

            {/* Image Indicators */}
            {room.images.length > 1 && (
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
                data-oid=":ud2ddv"
              >
                {room.images.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all duration-300",
                      index === currentImageIndex
                        ? "bg-white scale-125"
                        : "bg-white/50 hover:bg-white/75",
                    )}
                    onClick={() => handleImageClick(index)}
                    aria-label={`Go to image ${index + 1}`}
                    data-oid="u7r85d2"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Content */}
          <div
            className="flex flex-col gap-8 min-w-0 overflow-hidden pr-4 lg:pr-6 xl:pr-8"
            data-oid="ermox3u"
          >
            {/* Desktop Header - Shown only on desktop */}
            <div className="hidden lg:block" data-oid="6qg8_i0">
              <div className="mb-4" data-oid="6m75puu">
                <h2
                  className="text-3xl xl:text-4xl 2xl:text-5xl font-playfair text-gray-900"
                  data-oid="jfvb3s4"
                >
                  {t("rooms.tripleRoom")}
                </h2>
              </div>

              {/* Key badges below title - Desktop */}
              <div
                className="flex items-center gap-2 lg:gap-3 xl:gap-4 2xl:gap-6 flex-wrap"
                data-oid="deu_xy0"
              >
                <div className="flex items-center gap-2" data-oid="vzv0ivn">
                  <Users
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="bkkfne7"
                  />

                  <span className="text-sm text-gray-900" data-oid=":g-5ew8">
                    {featureBadges[0].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="_8lso.1">
                  <Square
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="khgc-ww"
                  />

                  <span className="text-sm text-gray-900" data-oid="dxcfe32">
                    {featureBadges[1].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="26xqd2k">
                  <Bed
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="7j9oi0r"
                  />

                  <span className="text-sm text-gray-900" data-oid="bb7we3h">
                    {featureBadges[2].value}
                  </span>
                </div>
              </div>
            </div>

            {/* 1. Description */}
            <div className="overflow-hidden" data-oid="53ga60e">
              <p
                className="text-sm lg:text-base xl:text-lg text-gray-700 leading-relaxed break-words"
                data-oid="klt49x4"
              >
                <span className="sm:hidden" data-oid="xqxmokr">
                  Spacious triple rooms with flexible bedding options, perfect
                  for families or small groups. Choose between mountain views of
                  Eiger, Mönch & Jungfrau or Lake Thun views.
                </span>
                <span className="hidden sm:inline" data-oid="7qz5tq:">
                  {t("rooms.tripleRoomDescription.desktop")}
                </span>
              </p>
            </div>

            {/* 2. All Features & Amenities - Consolidated */}
            <div className="grid grid-cols-1 gap-y-4" data-oid="ef4icu_">
              {/* Perfect for small families */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="ru:jocs"
              >
                <Users
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="q1jeu51"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="bfkpdbk"
                >
                  {t("rooms.tripleRoomFeatures.perfectForSmallFamilies")}
                </span>
              </div>

              {/* Scenic balcony with views */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="c97ghv:"
              >
                <Mountain
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="_j9_et_"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="6496a:9"
                >
                  {t("rooms.tripleRoomFeatures.scenicBalconyWithViews")}
                </span>
              </div>

              {/* Free Wifi */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="7ixdn0j"
              >
                <Wifi
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="hp_xhmo"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="p9030g9"
                >
                  Free Wifi
                </span>
              </div>

              {/* Flatscreen TV */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="pz0y8tq"
              >
                <Tv
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="fig83gc"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="9_ef.c3"
                >
                  Flatscreen TV
                </span>
              </div>

              {/* Alpine views */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="et-0vmq"
              >
                <Mountain
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid=":i29m-t"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="2q0ggfn"
                >
                  Alpine views
                </span>
              </div>

              {/* Premium organic bio cotton bedding */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="crgk.tr"
              >
                <Star
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="ll.gja9"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="0-oj6i4"
                >
                  Premium organic bio cotton bedding
                </span>
              </div>
            </div>

            {/* 3. Book Now and Share Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              data-oid="iijtd92"
            >
              <Button
                asChild
                size="lg"
                className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                trackingType="booking"
                trackingLabel={t("nav.bookNow")}
                trackingLocation="Triple Room Section"
                data-oid="6mtwi1y"
              >
                <a
                  href={import.meta.env.VITE_BOOKING_URL}
                  data-oid="-9-635q"
                >
                  {t("nav.bookNow")}
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="bg-white/80 hover:bg-alpine-gold hover:text-white border-gray-300 hover:border-alpine-gold transition-all duration-300 px-6 py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105"
                onClick={handleShare}
                aria-label="Share Triple Rooms"
                data-oid="bln7-d_"
              >
                <Share2 className="w-4 h-4 mr-2" data-oid="783ydo3" />
                {t("common.share")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TripleRoomSection;
